import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { CashierTradingAccount } from "@/api/cashier";
import {
  getSavedPaymentAccountsProfitOptions,
  getTradingAccountRefundBalanceOptions,
  withdrawalPaymentMethodsOptions,
} from "@/api/cashier/@tanstack/react-query.gen";
import type { RefundMethodIdsByLedgerId } from "@/pages/withdrawals/types";
import { addPreselectedLedgerSiblings } from "@/utils/refundPreselect";

export const REFUND_BALANCE_REFETCH_INTERVAL_MS = 30_000;

export const useWithdrawalData = (account?: CashierTradingAccount) => {
  const {
    data: balance,
    isLoading: balanceIsLoading,
    error: balanceError,
    refetch: balanceRefetch,
    dataUpdatedAt: balanceDataUpdatedAt,
  } = useQuery({
    ...getTradingAccountRefundBalanceOptions({
      path: { tradingAccountID: account?.id ?? "" },
    }),
    enabled: !!account,
    refetchInterval: REFUND_BALANCE_REFETCH_INTERVAL_MS,
  });

  const refundMethods = balance?.refund_accounts;

  const {
    data: profitMethods,
    isLoading: profitMethodsIsLoading,
    error: profitMethodsError,
    refetch: profitMethodsRefetch,
  } = useQuery({
    ...getSavedPaymentAccountsProfitOptions({
      path: { tradingAccountID: account?.id ?? "" },
    }),
    enabled: !!account,
  });

  const {
    data: newProfitMethods,
    isLoading: newProfitMethodsIsLoading,
    error: newProfitMethodsError,
    refetch: newProfitMethodsRefetch,
  } = useQuery({
    ...withdrawalPaymentMethodsOptions({
      path: {
        tradingAccountID: account?.id ?? "",
      },
    }),
    enabled: !!account,
  });

  const refetch = useCallback(() => {
    Promise.all([
      balanceRefetch(),
      profitMethodsRefetch(),
      newProfitMethodsRefetch(),
    ]).then(() => {});
  }, [balanceRefetch, newProfitMethodsRefetch, profitMethodsRefetch]);

  const refundMethodsByLedger = useMemo(() => {
    if (!refundMethods) return undefined;

    const byLedger = refundMethods.reduce<RefundMethodIdsByLedgerId>(
      (acc, method) => {
        const refundMethodId = method.id ?? method.payment_method_id;
        if (!refundMethodId) {
          return acc;
        }

        for (const entry of method.ledger_entries) {
          acc[entry.ledger_id] ??= new Set();
          acc[entry.ledger_id].add(refundMethodId);
        }
        return acc;
      },
      {} as RefundMethodIdsByLedgerId
    );

    // Pre-selected refund PMs (e.g. zota) render a `${pmId}`-keyed add-new card;
    // register it as a ledger sibling so per-card maxima reduce correctly.
    return addPreselectedLedgerSiblings(byLedger, refundMethods);
  }, [refundMethods]);

  const error = balanceError ?? profitMethodsError ?? newProfitMethodsError;

  const methodsIsLoading = profitMethodsIsLoading || newProfitMethodsIsLoading;

  return {
    balance,
    balanceIsLoading,
    balanceDataUpdatedAt,
    refundMethods,
    refundMethodsByLedger,
    profitMethods,
    newProfitMethods,
    methodsIsLoading,
    profitMethodsRefetch,
    refetch,
    error,
  };
};
